import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { WorkflowStatusType } from '@giteeteam/apps-team-components/dist/lib/global';
import {
  AnchorLocations,
  ArrowOverlay,
  BlankEndpoint,
  BrowserUIReact,
  DEFAULT,
  EVENT_CLICK,
  EVENT_TAP,
  JsPlumbToolkitSurfaceComponent,
  LabelOverlay,
  PanZoomOptions,
  Surface,
  SurfaceRenderOptions,
} from '@jsplumbtoolkit/browser-ui-react';
import * as ConnectorEditors from '@jsplumbtoolkit/connector-editors';
import * as OrthogonalConnectorEditor from '@jsplumbtoolkit/connector-editors-orthogonal';
import { OrthogonalConnector } from '@jsplumbtoolkit/connector-orthogonal';
import { AbsoluteLayout, Base, Edge, EVENT_NODE_MOVE_START, Vertex } from '@jsplumbtoolkit/core';
import { message } from 'antd';
import debug from 'debug';
import { cloneDeep, round, uniq } from 'lodash-es';

import { classnames } from 'lib/classnames';
import useI18n from 'lib/hooks/useI18n';
import { NodeProps, TransitionProps } from 'lib/types/workflow';
import { useWorkflowState } from 'lib/useWorkflow';
import { ElementEnum, NodeEnum } from 'lib/workflow';

import { SLIDER_NUM } from './controls/slider';
import Port from './nodes/Port';
import StartNode from './nodes/StartNode';
import TaskNode from './nodes/TaskNode';
import {
  ANY_TRANSITION_PREFIX,
  Colors,
  DEFAULT_ZOOM,
  ErrorCss,
  NodeRect,
  PlumbData,
  PlumbEdge,
  PlumbNode,
  StartNodeData,
} from './config';
import { WorkflowEditorSlider } from './controls';
import { StatusPanel } from './status';

import '@jsplumbtoolkit/browser-ui/css/jsplumbtoolkit.css';
import '@jsplumbtoolkit/connector-editors/css/jsplumbtoolkit-connector-editors.css';
import styles from './Palette.module.less';

const cx = classnames(styles);
const logMsg = debug('components:PlumbPalette');

type ObjectData = Record<string, any>;

export interface RefProps {
  clearSelection: () => void;
  deleteItem: (id: string, type: string, callback?: () => void) => void;
  updateItemProperties: (id: string, type: string, data: Record<string, any>) => void;
  setErrorTarget: (nodeIds: string, transitionIds: string[]) => void;
  clearErrorTarget: (data: { nodeId?: string; transitionId?: string }) => void;
  getData: () => { transitions: TransitionProps[]; nodes: NodeProps[] };
}

interface PaletteProps {
  initialData: PlumbData;
  draggedStatus: string[];
  addItem: (item: PlumbEdge | PlumbNode) => void;
  updateItem: (element?: NodeProps | TransitionProps, updateForceNode?: boolean) => void;
  transitions: TransitionProps[];
}

OrthogonalConnectorEditor.initialize();

const Palette: React.ForwardRefRenderFunction<RefProps, PaletteProps> = (
  { initialData, updateItem, draggedStatus, addItem, transitions },
  ref,
) => {
  const { t } = useI18n();
  const [surface, setSurface] = useState<Surface>(null);
  const pathEditor = useRef(null);
  const workflowWrapper = useRef(null);
  const [statusList, statusLoading, getStatusList] = useWorkflowState();
  const toolkitInstance = useRef<BrowserUIReact>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  // 放开连接时清楚connecting样式
  useEffect(() => {
    const wrapControl = workflowWrapper.current;
    if (!wrapControl) return;
    const removeClass = () => {
      wrapControl.className = workflowWrapper.current.className.replace(' connecting', '');
    };
    wrapControl.addEventListener('mouseup', removeClass);
    return () => {
      wrapControl.removeEventListener('mouseup', removeClass);
    };
  }, []);

  const clearAllSelectedAny = useCallback(() => {
    const nodes = toolkitInstance.current.getNodes();
    nodes.forEach(node => {
      toolkitInstance.current.updateNode(node, { selectedAny: false });
    });
  }, []);

  // 清空所有的选择
  const clearSelection = useCallback(() => {
    toolkitInstance.current.clearSelection();
    clearAllSelectedAny();
    pathEditor.current.stopEditing();
    updateItem(null, true);
  }, [clearAllSelectedAny, updateItem]);

  useEffect(() => {
    toolkitInstance.current = new BrowserUIReact({
      nodeFactory: (type: string, data: ObjectData, callback: (o: ObjectData) => any) => {
        logMsg('nodeFactory msg:', type, data, callback);
        callback(data);
        addItem(data as PlumbNode);
        return true;
      },
      edgeFactory: (
        type: string,
        data: ObjectData,
        continueCallback: (o: ObjectData) => any,
        _abortCallback: () => void,
        params: ObjectData,
      ): boolean => {
        logMsg('edgeFactory msg:', type, data, params);
        data.label = '';
        if (params.source.parent.id === StartNodeData.id) {
          // 开始连线自动命名
          data.label = t('pages.workflow.default.createNode');
        }
        data.elementType = ElementEnum.Edge;
        continueCallback(data);
        const edgeData = {
          source: `${params.edge.source.parent.id}.${params.edge.source.id}`,
          target: `${params.edge.target.parent.id}.${params.edge.target.id}`,
          data: params.edge.data,
          id: params.edge.id,
        };
        params.edge.data.id = params.edge.id;

        addItem(edgeData as PlumbEdge);
        pathEditor.current?.startEditing(params.edge, {});
        return true;
      },
      // 开始连接
      beforeStartConnect: () => {
        clearSelection();
        logMsg('beforeStartConnect:', workflowWrapper.current);
        workflowWrapper.current.className += ' connecting';
        return true;
      },
      // 开始拖动已有节点
      beforeStartDetach: () => {
        clearSelection();
        logMsg('beforeStartDetach:', workflowWrapper.current);
        workflowWrapper.current.className += ' connecting';
      },
      // 连接完成确认
      beforeConnect: (source: Vertex & { parent: Edge }, target: Vertex & { parent: Edge }): boolean => {
        logMsg('beforeConnect:', source, target);
        const edges = toolkitInstance.current.getAllEdges();
        logMsg('all edges:', edges);
        if (
          source.parent.id === StartNodeData.id &&
          edges.find((edge: Edge & { source: { parent: Edge } }) => edge.source.parent.id === StartNodeData.id)
        ) {
          // 如果开始节点如果连接了状态，不允许连接第二个状态
          message.warning(t('pages.workflow.message.noManyTarget'));
          return false;
        }
        if (target.parent.id === StartNodeData.id) {
          // 目标节点不能是开始节点
          message.warning(t('pages.workflow.message.noTarget'));
          return false;
        }
        // 如果起始点和终点是一样的，忽略
        if (target.id === source.id) return false;
        // 如果存在一样端点的连线，忽略
        const hasTransition = edges.find(edge => edge.source.id === source.id && edge.target.id === target.id);
        if (hasTransition) {
          return false;
        }
        return true;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTransitionClass = useCallback((transitionId: string) => {
    const renderer = toolkitInstance.current.getRenderers();
    const overlays = renderer.values().next().value.connMap[transitionId].getOverlays();
    for (const overlayKey in overlays) {
      if (overlays[overlayKey].type === 'Label') {
        overlays[overlayKey].canvas.className += ErrorCss;
      }
    }
  }, []);

  const anyClick = useCallback(
    (data: any) => {
      clearSelection();
      const transition = transitions.find(t => t.id === `${ANY_TRANSITION_PREFIX}${data.id}`);
      updateItem(transition as TransitionProps);
      toolkitInstance.current.updateNode(data.id, { selectedAny: true });
    },
    [clearSelection, transitions, updateItem],
  );

  const removeTransitionClass = useCallback((transitionId: string) => {
    const renderer = toolkitInstance.current.getRenderers();
    const overlays = renderer.values().next().value.connMap[transitionId].getOverlays();
    for (const overlayKey in overlays) {
      if (overlays[overlayKey].type === 'Label') {
        const regex = new RegExp(ErrorCss, 'g');
        overlays[overlayKey].canvas.className = overlays[overlayKey].canvas.className.replace(regex, '');
      }
    }
  }, []);

  const selectedNode = useCallback(
    node => {
      if (!node) {
        updateItem(null, true);
        return;
      }
      node.selected = true;
      updateItem(node);
    },
    [updateItem],
  );

  const view = {
    nodes: {
      // 设置节点当前点击事件
      selectable: {
        events: {
          [EVENT_TAP]: (params: { obj: Base; e: MouseEvent }) => {
            logMsg('node click', params);
            // 开始节点点击不做处理
            if (params.obj.data.id === StartNodeData.id) return;
            pathEditor.current.stopEditing();
            if (!params.e.altKey) {
              clearSelection();
            }
            toolkitInstance.current.toggleSelection(params.obj as Base);
            const selectedNodes = toolkitInstance.current.getSelection().getNodes();
            // 选择多个节点隐藏右侧框
            if (selectedNodes.length === 1) {
              selectedNode(cloneDeep(selectedNodes[0].data));
            } else {
              selectedNode(null);
            }
            toolkitInstance.current.updateNode(params.obj.data.id, {
              errorFlag: false,
            });
          },
        },
      },
      [NodeEnum.Start]: {
        parent: 'selectable',
        jsx: (ctx: any) => {
          return <StartNode ctx={ctx} {...ctx} />;
        },
      },
      [NodeEnum.Task]: {
        parent: 'selectable',
        jsx: (ctx: any) => {
          return <TaskNode ctx={ctx} {...ctx} anyClick={() => anyClick(ctx.data)} />;
        },
      },
    },
    edges: {
      [DEFAULT]: {
        endpoint: {
          type: BlankEndpoint.type,
          options: {
            radius: 4,
          },
        },
        // 线条连接样式-流程图样式
        connector: {
          type: OrthogonalConnector.type,
          options: { cornerRadius: 4 },
        },
        // 线条样式
        paintStyle: {
          strokeWidth: 1,
          stroke: Colors.Common,
          outlineWidth: 2,
          outlineStroke: 'blank',
        },
        hoverPaintStyle: { strokeWidth: 1, stroke: Colors.Hover },
        // 线条事件绑定(单击线条事件)
        events: {
          [EVENT_CLICK]: (params: any) => {
            logMsg('edge click', params);
            clearSelection();
            pathEditor.current?.startEditing(params.edge, {});
            const transition = params.edge;
            transition.elementType = ElementEnum.Edge;
            updateItem(transition);
            const id = params.edge.id;
            toolkitInstance.current.updateEdge(id, {
              color: Colors.Common,
              lineWidth: 1,
            });
            removeTransitionClass(id);
          },
        },
        // 线条箭头
        overlays: [
          {
            type: ArrowOverlay.type,
            options: {
              location: 1,
              width: 10,
              length: 10,
              foldback: 1,
            },
          },
          {
            type: LabelOverlay.type,
            options: {
              label: '${label}',
              events: {
                click: params => {
                  logMsg('label click', params);
                },
              },
            },
          },
        ],
      },
    },
    ports: {
      LeftTop: {
        component: Port,
        maxConnections: -1,
        anchor: [0.25, 0, 0, -1],
      },
      RightTop: {
        component: Port,
        maxConnections: -1,
        anchor: [0.75, 0, 0, -1],
      },
      LeftBottom: {
        component: Port,
        maxConnections: -1,
        anchor: [0.25, 1, 0, 1],
      },
      RightBottom: {
        component: Port,
        maxConnections: -1,
        anchor: [0.75, 1, 0, 1],
      },
      Top: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Top],
      },
      Right: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Right],
      },
      Left: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Left],
      },
      Bottom: {
        component: Port,
        maxConnections: -1,
        anchor: [AnchorLocations.Bottom],
      },
    },
  };

  const renderParams: SurfaceRenderOptions = {
    wheel: {
      zoom: false,
    },
    layout: {
      type: AbsoluteLayout.type,
    },
    events: {
      canvasClick: () => {
        clearSelection();
      },
      zoom: (data: PanZoomOptions) => {
        setZoom(data.zoom);
      },
      [EVENT_NODE_MOVE_START]: () => {
        pathEditor.current.stopEditing();
      },
    },
    consumeRightClick: false,
    zoomToFit: false,
    zoomRange: [SLIDER_NUM.MIN, SLIDER_NUM.MAX],
    zoom: 1,
    simpleEdgeStyles: true,
  };

  // 从流程图删除元素
  const deleteItemHandle = useCallback(
    (id: string, type: string) => {
      if (type === ElementEnum.Node) {
        // 状态节点元素
        const node = toolkitInstance.current.getNode(id);
        if (!node) {
          return;
        }
        toolkitInstance.current.removeNode(node);
      } else if (type === ElementEnum.Edge) {
        // 流转动作元素
        const edge = toolkitInstance.current.getEdge(id);
        toolkitInstance.current.removeEdge(edge);
      }
      updateItem();
    },
    [updateItem],
  );

  // 设置流程图的错误状态
  const setErrorTarget = useCallback(
    (nodeIds, transitionIds) => {
      nodeIds.forEach(id => {
        const node = toolkitInstance.current.getNode(id);
        if (node) {
          node.data.errorFlag = true;
          toolkitInstance.current.updateNode(node);
        }
      });
      transitionIds.forEach(id => {
        const edge = toolkitInstance.current.getEdge(id);
        if (edge) {
          toolkitInstance.current.updateEdge(id, {
            color: Colors.Error,
            lineWidth: 2,
          });
          addTransitionClass(id);
        }
      });
    },
    [addTransitionClass],
  );

  // 设置流程图的错误状态
  const clearErrorTarget = useCallback(
    ({ nodeId, transitionId }) => {
      if (nodeId) {
        const node = toolkitInstance.current.getNode(nodeId);
        if (node) {
          node.data.errorFlag = false;
          toolkitInstance.current.updateNode(node);
        }
      }
      if (transitionId) {
        const edge = toolkitInstance.current.getEdge(transitionId);
        if (edge) {
          toolkitInstance.current.updateEdge(transitionId, {
            color: Colors.Common,
            lineWidth: 1,
          });
          removeTransitionClass(transitionId);
        }
      }
    },
    [removeTransitionClass],
  );

  // 从流程图更新元素
  const updateItemHandle = useCallback((id: string, type: string, data: Record<string, any>) => {
    logMsg('update item data:', id, type, data);
    if (type === ElementEnum.Node) {
      // 更新Node
      const node = toolkitInstance.current.getNode(id);
      if (node) {
        toolkitInstance.current.updateNode(node, data);
      }
    } else if (type === ElementEnum.Edge) {
      // 更新Edge
      const edge = toolkitInstance.current.getEdge(id);
      if (edge) {
        edge.data.label = data.label;
        toolkitInstance.current.updateEdge(edge);
      }
    }
  }, []);

  useImperativeHandle(ref, () => {
    return {
      clearSelection,
      setErrorTarget,
      clearErrorTarget,
      deleteItem: deleteItemHandle,
      updateItemProperties: updateItemHandle,
      getData: () => toolkitInstance.current.toJSON(),
    };
  }, [clearErrorTarget, clearSelection, deleteItemHandle, setErrorTarget, updateItemHandle]);

  // 从侧边拖拽节点到流程图
  const dataGenerator = useCallback((element: Element) => {
    const data: PlumbNode = {
      w: NodeRect.width,
      h: NodeRect.height,
      key: element.getAttribute('data-node-key') as WorkflowStatusType,
      type: NodeEnum.Task,
      name: element.getAttribute('title'),
      id: element.getAttribute('data-node-id'),
      elementType: ElementEnum.Node,
      anyTag: false,
      selectedAny: false,
    };
    return data;
  }, []);

  useEffect(() => {
    if (surface) {
      pathEditor.current = ConnectorEditors.newInstance(surface);
    }
  }, [surface]);

  useEffect(() => {
    // 初始化更新状态
    if (toolkitInstance.current && surface && initialData?.nodes.length) {
      setTimeout(() => {
        const ports = [];
        initialData.edges.forEach(edge => {
          ports.push(edge.source.split('.')[1]);
          ports.push(edge.target.split('.')[1]);
        });
        initialData.ports = uniq(ports).map(port => ({ id: port }));
        logMsg('load data:', toolkitInstance.current, initialData, surface);
        toolkitInstance.current.load({
          data: initialData,
        });
      }, 500);
    }
  }, [initialData, surface]);

  useEffect(() => {
    const wheelChange = (e: WheelEvent) => {
      setZoom(zoom => {
        const zoomValue = Math.min(Math.max(round(zoom - e.deltaY / 300, 2), SLIDER_NUM.MIN), SLIDER_NUM.MAX);
        surface.setZoom(zoomValue);
        return zoomValue;
      });
    };
    if (surface) {
      const jtk = document.querySelector('.jtk-draggable');
      jtk?.addEventListener('wheel', wheelChange);
    }
    return () => {
      const jtk = document.querySelector('.jtk-draggable');
      return jtk?.removeEventListener('wheel', wheelChange);
    };
  }, [surface]);

  return (
    <div className={cx('workflow-palette-container')}>
      {surface && (
        <StatusPanel
          surface={surface}
          list={statusList}
          loading={statusLoading}
          getList={getStatusList}
          draggedStatus={draggedStatus}
          dataGenerator={dataGenerator}
        />
      )}
      <div className={cx('workflow-wrapper')} ref={workflowWrapper}>
        {toolkitInstance.current && (
          <JsPlumbToolkitSurfaceComponent
            toolkit={toolkitInstance.current}
            view={view}
            renderParams={renderParams}
            ref={(data: JsPlumbToolkitSurfaceComponent) => {
              if (data != null) {
                setSurface(data.surface);
              }
            }}
          />
        )}
      </div>
      <div className={cx('slider-wrapper')}>
        {surface && <WorkflowEditorSlider zoom={zoom} setZoom={setZoom} surface={surface} />}
      </div>
    </div>
  );
};

export default forwardRef(Palette);
